import type { MouseEvent } from 'react';
import { useRef } from 'react';
import styled from 'styled-components';
import { Avatar, IconButton2, ListItemContent } from '@frameio/vapor';
import { IconMoreHorizontal } from '@frameio/vapor-icons';
import { useOpenWorkspaceProjectMenu } from '../../../hooks';
import { useWorkspaceNavigationItemInfoFragment } from '../gql/generated/WorkspaceNavigationItemInfo';
import { DefaultAvatar } from './DefaultAvatar';
import TEXT from './generated/TEXT/Workspace';

type ItemProps = {
  workspaceId: string;
  contextMenuDisabled?: boolean;
};

export const Workspace = ({
  workspaceId,
  contextMenuDisabled = false,
}: ItemProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const openContextMenu = useOpenWorkspaceProjectMenu();
  const { data } = useWorkspaceNavigationItemInfoFragment(workspaceId);

  const handleContextMenu = (event?: MouseEvent) => {
    if (contextMenuDisabled) return false;
    openContextMenu(
      { id: workspaceId, type: 'workspace' },
      containerRef,
      event
    );
    return true;
  };

  const WorkspaceAvatar = (
    <Avatar
      aria-label={data.name}
      background="transparent"
      shape="square"
      src={data.images?.image128 ?? undefined}
    >
      <DefaultAvatar />
    </Avatar>
  );

  const WorkspaceContextMenuButton = (
    <MenuButton
      data-testid="workspace-row--menu-button"
      icon={<IconMoreHorizontal />}
      label={TEXT.ariaLabel}
      onClick={handleContextMenu}
      size="small"
      tabIndex={0}
      variant="tertiary"
    />
  );
  return (
    <ListItemContainer
      ref={containerRef}
      onContextMenu={(e) => {
        e.preventDefault();
        handleContextMenu(e);
      }}
    >
      <WorkspaceListItem
        adornmentAfter={WorkspaceContextMenuButton}
        adornmentBefore={WorkspaceAvatar}
        label={data.name}
      />
    </ListItemContainer>
  );
};

const ListItemContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const MenuButton = styled(IconButton2)`
  visibility: hidden;
`;

const WorkspaceListItem = styled(ListItemContent)`
  &:hover ${MenuButton}, &[data-focused='true'] ${MenuButton} {
    visibility: visible;
  }
`;

TEXT.DEFINE({
  /** Aria label for the workspace options menu button */
  ariaLabel: 'More options',
});
